// Owner 2026-10-12: the legacy org chart (OrgChart.vb updateEmpStatus, a 30-second
// timer) drew a green circle on everyone who is clocked IN today and a red one on
// everyone else. This hook asks GET /api/OrgChart/presence for the same IN / OUT
// word per person, refreshes it every 30 seconds while the tab is visible, and
// the small components below draw the circle on a photo or a pill next to a name.
// All three chart views (Team, Tree, Compact) share it.
import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import "./presence.css";

export const PRESENCE_REFRESH_MS = 30000;

const EMPTY = { byId: {}, inCount: 0, outCount: 0, asOf: null, loaded: false, error: false };

const normalize = (data) => {
  const d = data || {};
  const people = d.people ?? d.People ?? [];
  const byId = {};
  (Array.isArray(people) ? people : []).forEach((p) => {
    const id = String(p.empId ?? p.EmpId ?? "").trim();
    if (!id) return;
    byId[id] = String(p.status ?? p.Status ?? "OUT").toUpperCase() === "IN" ? "IN" : "OUT";
  });
  return {
    byId,
    inCount: Number(d.in ?? d.In ?? 0),
    outCount: Number(d.out ?? d.Out ?? 0),
    asOf: new Date(),
    loaded: true,
    error: false,
  };
};

export default function useOrgPresence(enabled = true) {
  const [presence, setPresence] = useState(EMPTY);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;

    const schedule = () => {
      if (!alive) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(tick, PRESENCE_REFRESH_MS);
    };
    const load = async () => {
      try {
        const res = await api.get("OrgChart/presence");
        if (alive) setPresence(normalize(res.data));
      } catch (_) {
        if (alive) setPresence((prev) => ({ ...prev, error: true }));
      } finally {
        schedule();
      }
    };
    const tick = () => {
      // A hidden tab does not poll; it catches up the moment it is shown again.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") schedule();
      else load();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };

    load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);

  return presence;
}

export const presenceLabel = (status) =>
  status === "IN" ? "In the building (clocked in today)" : status === "OUT" ? "Out (not clocked in today)" : "Clock status loading";

const presenceClass = (status) => (status === "IN" ? "is-in" : status === "OUT" ? "is-out" : "is-unknown");

const escAttr = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");

/** The circle as an HTML string, for the d3 card template (Compact view). */
export const presenceDotHtml = (status, empId, extraClass = "") =>
  `<span class="oc-presence ${presenceClass(status)} ${extraClass}" data-presence-id="${escAttr(empId)}" title="${escAttr(presenceLabel(status))}"></span>`;

/** Re-colour circles already in the DOM (the Compact view updates them in place). */
export const applyPresenceToDom = (root, byId) => {
  if (!root) return;
  root.querySelectorAll("[data-presence-id]").forEach((el) => {
    const status = byId[el.getAttribute("data-presence-id")];
    el.classList.remove("is-in", "is-out", "is-unknown");
    el.classList.add(presenceClass(status));
    el.setAttribute("title", presenceLabel(status));
  });
};

/** The circle as a React element; place it inside a position:relative wrapper. */
export function PresenceDot({ status, size = "" }) {
  const sizeClass = size === "sm" ? " oc-presence--sm" : size === "lg" ? " oc-presence--lg" : "";
  return <span className={`oc-presence ${presenceClass(status)}${sizeClass}`} title={presenceLabel(status)} />;
}

/** "In the building" / "Out" pill for a detail panel. */
export function PresencePill({ status, className = "" }) {
  const text = status === "IN" ? "In the building" : status === "OUT" ? "Out" : "Clock status loading";
  return (
    <span className={`oc-presence-pill ${presenceClass(status)} ${className}`} title={presenceLabel(status)}>
      {text}
    </span>
  );
}

/** "12 in · 30 out" readout with the refresh time in the tooltip. */
export function PresenceReadout({ presence, className = "" }) {
  if (!presence?.loaded) return null;
  const at = presence.asOf ? presence.asOf.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
  const title = `From the time clock: green = clocked IN today, red = OUT. Refreshes every 30 seconds${at ? `, last at ${at}` : ""}.`;
  return (
    <span className={`oc-presence-readout ${className}`} title={title}>
      <span className="oc-presence is-in oc-presence--inline" />
      <strong>{presence.inCount}</strong>&nbsp;in
      <span className="oc-presence is-out oc-presence--inline ms-2" />
      <strong>{presence.outCount}</strong>&nbsp;out
    </span>
  );
}
